import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Ticket, 
  Search, 
  Filter, 
  RefreshCw, 
  MessageSquare, 
  Clock, 
  User,
  AlertCircle,
  CheckCircle,
  XCircle,
  Send,
  ChevronDown,
  X
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';

export default function AdminSupportTickets() {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [response, setResponse] = useState('');
  const [newStatus, setNewStatus] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchTickets();
  }, [statusFilter, priorityFilter]);

  const fetchTickets = async () => {
    setLoading(true);
    try {
      let url = `${API}/api/admin/support-tickets`;
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (priorityFilter !== 'all') params.append('priority', priorityFilter);
      if (params.toString()) url += `?${params.toString()}`;

      const res = await fetch(url, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setTickets(data.tickets || []);
      }
    } catch (err) {
      console.error('Error fetching tickets:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleRespond = async () => {
    if (!response.trim() || !newStatus) return;
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/api/admin/support-tickets/${selectedTicket.ticket_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          response: response,
          status: newStatus
        })
      });
      if (res.ok) {
        setShowModal(false);
        setResponse('');
        setNewStatus('');
        setSelectedTicket(null);
        fetchTickets();
      }
    } catch (err) {
      console.error('Error responding to ticket:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const openTicket = (ticket) => {
    setSelectedTicket(ticket);
    setNewStatus(ticket.status);
    setShowModal(true);
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleString('en-US', { 
      month: 'short', 
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const statusColors = {
    open: { bg: 'bg-blue-100', text: 'text-blue-700', icon: AlertCircle },
    in_progress: { bg: 'bg-amber-100', text: 'text-amber-700', icon: Clock },
    resolved: { bg: 'bg-green-100', text: 'text-green-700', icon: CheckCircle },
    closed: { bg: 'bg-slate-100', text: 'text-slate-700', icon: XCircle }
  };

  const priorityColors = {
    low: 'bg-slate-100 text-slate-600',
    medium: 'bg-blue-100 text-blue-600',
    high: 'bg-orange-100 text-orange-600',
    urgent: 'bg-red-100 text-red-600'
  };

  const filteredTickets = tickets.filter(ticket => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      ticket.ticket_id.toLowerCase().includes(query) ||
      ticket.subject.toLowerCase().includes(query) ||
      ticket.created_by_name?.toLowerCase().includes(query) ||
      ticket.description.toLowerCase().includes(query)
    );
  });

  const stats = {
    total: tickets.length,
    open: tickets.filter(t => t.status === 'open').length,
    inProgress: tickets.filter(t => t.status === 'in_progress').length,
    resolved: tickets.filter(t => t.status === 'resolved').length
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-3">
            <Ticket className="w-7 h-7 text-blue-500" />
            Support Tickets
          </h1>
          <p className="text-slate-500 mt-1">Manage and respond to staff support requests</p>
        </div>
        <button
          onClick={fetchTickets}
          className="px-4 py-2 bg-white border border-slate-200 rounded-xl text-sm font-medium text-slate-600 hover:bg-slate-50 flex items-center gap-2"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <p className="text-sm text-slate-500">Total Tickets</p>
          <p className="text-2xl font-bold text-slate-800">{stats.total}</p>
        </div>
        <div className="bg-blue-50 rounded-xl border border-blue-200 p-4">
          <p className="text-sm text-blue-600">Open</p>
          <p className="text-2xl font-bold text-blue-700">{stats.open}</p>
        </div>
        <div className="bg-amber-50 rounded-xl border border-amber-200 p-4">
          <p className="text-sm text-amber-600">In Progress</p>
          <p className="text-2xl font-bold text-amber-700">{stats.inProgress}</p>
        </div>
        <div className="bg-green-50 rounded-xl border border-green-200 p-4">
          <p className="text-sm text-green-600">Resolved</p>
          <p className="text-2xl font-bold text-green-700">{stats.resolved}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-slate-200 p-4 flex flex-col sm:flex-row gap-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            placeholder="Search tickets..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg text-sm"
          />
        </div>
        <div className="flex gap-3">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 border border-slate-200 rounded-lg text-sm"
          >
            <option value="all">All Status</option>
            <option value="open">Open</option>
            <option value="in_progress">In Progress</option>
            <option value="resolved">Resolved</option>
            <option value="closed">Closed</option>
          </select>
          <select
            value={priorityFilter}
            onChange={(e) => setPriorityFilter(e.target.value)}
            className="px-3 py-2 border border-slate-200 rounded-lg text-sm"
          >
            <option value="all">All Priority</option>
            <option value="urgent">Urgent</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </select>
        </div>
      </div>

      {/* Tickets List */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="w-6 h-6 animate-spin text-blue-500" />
          </div>
        ) : filteredTickets.length === 0 ? (
          <div className="text-center py-12">
            <Ticket className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">No tickets found</p>
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {filteredTickets.map((ticket) => {
              const StatusIcon = statusColors[ticket.status]?.icon || AlertCircle;
              return (
                <motion.div
                  key={ticket.ticket_id}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  whileHover={{ backgroundColor: '#f8fafc' }}
                  onClick={() => openTicket(ticket)}
                  className="p-4 cursor-pointer"
                >
                  <div className="flex items-start gap-4">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${statusColors[ticket.status]?.bg}`}>
                      <StatusIcon className={`w-5 h-5 ${statusColors[ticket.status]?.text}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs font-mono text-slate-400">{ticket.ticket_id}</span>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${priorityColors[ticket.priority]}`}>
                          {ticket.priority}
                        </span>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium capitalize ${statusColors[ticket.status]?.bg} ${statusColors[ticket.status]?.text}`}>
                          {ticket.status.replace('_', ' ')}
                        </span>
                      </div>
                      <h3 className="font-medium text-slate-800 truncate">{ticket.subject}</h3>
                      <p className="text-sm text-slate-500 truncate">{ticket.description}</p>
                      <div className="flex items-center gap-4 mt-2 text-xs text-slate-400">
                        <span className="flex items-center gap-1">
                          <User className="w-3 h-3" />
                          {ticket.created_by_name || ticket.created_by}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {formatDate(ticket.created_at)}
                        </span>
                        {ticket.responses?.length > 0 && (
                          <span className="flex items-center gap-1 text-green-500">
                            <MessageSquare className="w-3 h-3" />
                            {ticket.responses.length} response(s)
                          </span>
                        )}
                      </div>
                    </div>
                    <ChevronDown className="w-5 h-5 text-slate-300" />
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>

      {/* Ticket Detail Modal */}
      <AnimatePresence>
        {showModal && selectedTicket && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-2xl w-full max-w-2xl max-h-[85vh] overflow-hidden flex flex-col"
            >
              {/* Modal Header */}
              <div className="p-6 border-b border-slate-200">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-sm font-mono text-slate-400">{selectedTicket.ticket_id}</span>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${priorityColors[selectedTicket.priority]}`}>
                        {selectedTicket.priority}
                      </span>
                    </div>
                    <h2 className="text-xl font-bold text-slate-800">{selectedTicket.subject}</h2>
                    <p className="text-sm text-slate-500 mt-1">
                      From: {selectedTicket.created_by_name} • {formatDate(selectedTicket.created_at)}
                    </p>
                  </div>
                  <button 
                    onClick={() => setShowModal(false)}
                    className="p-2 hover:bg-slate-100 rounded-lg text-slate-400"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* Modal Body */}
              <div className="p-6 overflow-y-auto flex-1">
                {/* Original Issue */}
                <div className="mb-6">
                  <h3 className="text-sm font-medium text-slate-700 mb-2">Issue Description</h3>
                  <div className="bg-slate-50 rounded-xl p-4">
                    <p className="text-sm text-slate-600 whitespace-pre-wrap">{selectedTicket.description}</p>
                    <p className="text-xs text-slate-400 mt-2">Issue Type: {selectedTicket.issue_type}</p>
                  </div>
                </div>

                {/* Previous Responses */}
                {selectedTicket.responses?.length > 0 && (
                  <div className="mb-6">
                    <h3 className="text-sm font-medium text-slate-700 mb-2">Response History</h3>
                    <div className="space-y-3">
                      {selectedTicket.responses.map((resp, idx) => (
                        <div key={idx} className="bg-green-50 border border-green-200 rounded-xl p-4">
                          <p className="text-sm text-slate-700 whitespace-pre-wrap">{resp.response}</p>
                          <p className="text-xs text-green-600 mt-2">
                            {resp.responded_by} • {formatDate(resp.responded_at)}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Response Form */}
                <div>
                  <h3 className="text-sm font-medium text-slate-700 mb-2">Add Response</h3>
                  <textarea
                    value={response}
                    onChange={(e) => setResponse(e.target.value)}
                    placeholder="Type your response..."
                    rows={4}
                    className="w-full px-4 py-3 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                  />
                  
                  <div className="mt-4">
                    <label className="text-sm font-medium text-slate-700 mb-2 block">Update Status</label>
                    <div className="flex gap-2 flex-wrap">
                      {['open', 'in_progress', 'resolved', 'closed'].map((status) => (
                        <button
                          key={status}
                          onClick={() => setNewStatus(status)}
                          className={`px-4 py-2 rounded-lg text-sm font-medium capitalize transition-all ${
                            newStatus === status
                              ? `${statusColors[status]?.bg} ${statusColors[status]?.text} ring-2 ring-offset-2 ring-blue-500`
                              : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                          }`}
                        >
                          {status.replace('_', ' ')}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Modal Footer */}
              <div className="p-4 border-t border-slate-200 bg-slate-50 flex items-center justify-end gap-3">
                <button
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 bg-white border border-slate-200 text-slate-600 rounded-lg text-sm font-medium hover:bg-slate-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleRespond}
                  disabled={!response.trim() || !newStatus || submitting}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 text-white rounded-lg text-sm font-medium flex items-center gap-2"
                >
                  {submitting ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4" />
                  )}
                  Send Response
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
